import { useState, useEffect, useCallback } from "react";
import {
    Box, Button, Checkbox, FormControl, FormControlLabel, FormHelperText,
    Grid2, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography, Card,
    CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
    Icon, Tooltip
} from "@mui/material";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import CommonPagination from "../../components/CommonPagination";
import {
    DeactivateClientUrl,
    DeleteClientUrl,
    GetClientDetailsUrl,
    DeleteDashboardUserUrl,
    getFillListUrl,
    UpdateUserDetailsUrl,
    InsertUserDetailsUrl,
    dashboardUserCountryActionID
} from "../../utils/constants";
import { axiosDelete, axiosGet, axiosPost } from "../../utils/Axios";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useToast } from "../../components/toast";
import { HeaderBlock } from "@colliers/colliers-b2c";
import {
    isAdminRole,
    isDeleteAccountSuccess,
    NO_DATA_FOUND_MSG,
    normalizeClientStatus,
    resolveDeleteErrorMessage
} from "./clientDisableOptions";

const LOGIN_ROOT_DATA_PATH = "/login/root-data";
const CLIENT_LIST_PATH = "/client-user-management";
const DIALOG_BTN_CLASS = "text-neutral-7 border-size-s border-radius-soft add-dashbaord-btn";
const SECTION_HEADER_CLASS = "font-size-h5 text-primary margin-bottom-xs text-uppercase padding-top-xs clientColor";
const STAT_CARD_CLASS = "text-align-center border-light border-radius-soft padding-xs padding-y-base";
const READONLY_FIELD_CLASS = "clientFormField clientReadonlyField";
const READONLY_FIELD_SLOT_PROPS = {
    inputLabel: {
        shrink: true,
    },
    input: {
        readOnly: true,
    },
};

const isValidCountryID = (countryID) => {
    return countryID !== "" && countryID !== null && countryID !== "0";
};

const is_email = (str) => {
    if (typeof str !== "string" || str.length > 320) {
        return false;
    }
    const validRegex = /^([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*|"[^"]+")@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/;
    return validRegex.test(str);
};

const compareStringIds = (string1, string2) => {
    const array1 = string1.split(",").map(Number);
    const array2 = string2.split(",").map(Number);

    if (array1.length !== array2.length) return true;

    const sortedArray1 = [...array1].sort((a, b) => a - b);
    const sortedArray2 = [...array2].sort((a, b) => a - b);

    for (let i = 0; i < sortedArray1.length; i++) {
        if (sortedArray1[i] !== sortedArray2[i]) {
            return true;
        }
    }
    return false;
};

const createUserPayload = (email, countryID, countryAccess, clientID, originalIds) => {
    const extractedBaseCountryID = clientID > 0 && countryID && countryID !== "0"
        ? Number(countryID)
        : 0;

    const payload = {
        name: null,
        emailAddress: email || "",
        roleID: clientID > 0 ? 2 : 0,
        basecountryID: extractedBaseCountryID,
        updateuserID: clientID > 0 ? clientID : 0,
        accessRight: null,
        countryAccess: countryAccess,
    };

    if (clientID > 0) {
        payload.isCountryChanged = compareStringIds(countryAccess, originalIds);
        payload.isAccessChanged = false;
    }

    return payload;
};

const formatValidUntilDate = (date, invalidDateText) => {
    if (!date) return "";

    const parsedDate = moment(date, ["DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY", moment.ISO_8601]);
    return parsedDate.isValid() ? parsedDate.format("DD MMM YYYY") : invalidDateText;
};

const formatLastLoginDate = (rawDate, invalidDateText, neverLoggedInText) =>
    rawDate ? formatValidUntilDate(rawDate, invalidDateText) : neverLoggedInText;

const getEditClientID = (location) => location.state?.userID || null;

const buildAccessCountriesFromResponse = (userCountries, updatedAccessCountries) => {
    if (!userCountries?.length) return null;
    const countryIdArray = userCountries.map(country => Number.parseInt(country.countryID, 10));
    const newAccessCountries = updatedAccessCountries.map(item => ({
        ...item,
        defaultChecked: countryIdArray.includes(Number(item.code))
    }));
    return {
        newAccessCountries,
        originalIds: countryIdArray.join(",")
    };
};

const updateCountryCheckedState = (countries, targetCode, checked) =>
    countries.map((country) =>
        country.code === targetCode
            ? { ...country, defaultChecked: Boolean(checked) }
            : country
    );

const computeValidationState = ({
    validate,
    emailAddress,
    basecountryID,
    accessCountries,
    pleaseSelectCountryText,
    pleaseEnterEmailText
}) => {
    const isEmailInvalid = !emailAddress || !is_email(emailAddress);
    const hasSelectedAccessCountry = Boolean(accessCountries?.some(x => x.defaultChecked));
    const showEmailError = validate && isEmailInvalid;
    const showCountryError = validate && !isValidCountryID(basecountryID);
    const showAccessCountryError = validate && !hasSelectedAccessCountry;
    return {
        showEmailError,
        showCountryError,
        showAccessCountryError,
        errorMessageEmail: showEmailError ? pleaseEnterEmailText : "",
        errorMessageCountry: showCountryError ? pleaseSelectCountryText : ""
    };
};

const isClientFormValid = (emailAddress, countryID, countryAccess) => {
    const isEmailValid = Boolean(emailAddress) && is_email(emailAddress);
    if (isEmailValid && countryID > 0 && Boolean(countryAccess)) return true;
    console.error("Mandatory fields are required");
    return false;
};

const parseAndValidateUserInfo = (navigate) => {
    const userInfoString = localStorage.getItem("userInfo");
    if (!userInfoString) {
        navigate(LOGIN_ROOT_DATA_PATH);
        return false;
    }
    try {
        const userInfo = JSON.parse(userInfoString);
        console.log("Parsed userInfo:", userInfo);
        return true;
    } catch (error) {
        console.error("Failed to parse userInfo:", error);
        navigate(LOGIN_ROOT_DATA_PATH);
        return false;
    }
};

const loadClientDetailsData = async ({
    clientID,
    updatedAccessCountries = [],
    setIsLoading,
    setLastLoginDate,
    setStatus,
    resetClientState,
    setEmailAddress,
    setBasecountryID,
    setActiveDashboard,
    setViewedDashboard,
    formatLastLogin,
    setDashboardGrpList,
    setAccessCountries,
    setOriginalcountryIds
}) => {
    try {
        setIsLoading(true);
        setLastLoginDate("");
        setStatus("");
        const response = await axiosGet(`${GetClientDetailsUrl}?EditClientID=${clientID}`);
        if (!response) {
            console.error("Failed to fetch getClientDetails: Invalid response format.");
            resetClientState();
            return;
        }
        setEmailAddress(response.emailAddress);
        setBasecountryID(response.basecountryID);
        setActiveDashboard(response.activeDashboard);
        setViewedDashboard(response.viewedDashboard);
        setLastLoginDate(formatLastLogin(response.lastLoginDate));
        setStatus(normalizeClientStatus(response.status));
        setDashboardGrpList(response.clientDashboardGroup);
        const countryResult = buildAccessCountriesFromResponse(response.userCountries, updatedAccessCountries);
        if (countryResult) {
            setAccessCountries(countryResult.newAccessCountries);
            setOriginalcountryIds(countryResult.originalIds);
        }
    } catch (error) {
        console.error("Error fetching getClientDetails:", error);
        resetClientState();
    } finally {
        setIsLoading(false);
    }
};

const loadInitialClientData = async ({
    userID,
    setIsLoading,
    setClientCountryFillList,
    setAccessCountries,
    getClientDetails
}) => {
    setIsLoading(true);
    try {
        let updatedAccessCountries = [];
        const data = await axiosGet(getFillListUrl, {
            actionID: dashboardUserCountryActionID,
            valueID: userID,
            keyValue: null
        });
        if (data?.length > 0) {
            setClientCountryFillList(data);
            updatedAccessCountries = data.map(item => ({
                ...item,
                defaultChecked: false
            }));
            setAccessCountries(updatedAccessCountries);
        } else {
            console.error("Failed to fetch country list: No data found.");
        }
        await getClientDetails(userID, updatedAccessCountries);
    } catch (error) {
        console.error("Error fetching country list :", error);
    } finally {
        setIsLoading(false);
    }
};

const deactivateClientUser = async ({
    userID,
    deactivateAccountSuccessText,
    accessCountries,
    setIsLoading,
    setIsDeleteModal,
    setStatus,
    setActiveDashboard,
    setDashboardGrpList,
    showToast,
    getClientDetails
}) => {
    if (!userID) {
        return;
    }

    setIsLoading(true);
    try {
        const response = await axiosDelete(DeactivateClientUrl, { clientUserID: Number(userID) });
        if (response) {
            showToast("success", deactivateAccountSuccessText);
            await getClientDetails(userID, accessCountries);
            setStatus("Inactive");
            setActiveDashboard(0);
            setDashboardGrpList([]);
        } else {
            showToast("error", NO_DATA_FOUND_MSG);
        }
    } catch (error) {
        showToast("error", `Error deactivating client: ${error}`);
    } finally {
        setIsLoading(false);
        setIsDeleteModal(false);
    }
};

const deleteClientAccountPermanently = async ({
    userID,
    deleteAccountSuccessText,
    setIsLoading,
    setIsDeleteAccountModal,
    showToast,
    navigate
}) => {
    if (!userID) {
        return;
    }

    setIsLoading(true);
    try {
        const response = await axiosDelete(DeleteClientUrl, { clientUserID: Number(userID) });
        if (isDeleteAccountSuccess(response)) {
            showToast("success", deleteAccountSuccessText);
            navigate(CLIENT_LIST_PATH);
        } else {
            showToast("error", resolveDeleteErrorMessage(response));
        }
    } catch (error) {
        showToast("error", `Error deleting client: ${error}`);
    } finally {
        setIsLoading(false);
        setIsDeleteAccountModal(false);
    }
};

const revokeClientDashboardAccess = async ({
    dashboardID,
    editClientID,
    accessRevokedSuccessText,
    formatLastLogin,
    setIsLoading,
    setIsDeleteModal,
    setRevokeTarget,
    setLastLoginDate,
    setStatus,
    setDashboardGrpList,
    setActiveDashboard,
    setViewedDashboard,
    showToast
}) => {
    setIsLoading(true);
    try {
        const response = await axiosDelete(DeleteDashboardUserUrl, {
            deleteUserID: editClientID,
            deleteADGroupID: 0,
            dashboardID: dashboardID
        });
        if (!response) {
            showToast("error", "Failed to Delete");
            return;
        }
        showToast("success", accessRevokedSuccessText);
        setLastLoginDate("");
        setStatus("");
        const response1 = await axiosGet(`${GetClientDetailsUrl}?EditClientID=${editClientID}`);
        if (response1) {
            setDashboardGrpList(response1.clientDashboardGroup);
            setActiveDashboard(response1.activeDashboard);
            setViewedDashboard(response1.viewedDashboard);
            setLastLoginDate(formatLastLogin(response1.lastLoginDate));
            setStatus(normalizeClientStatus(response1.status));
        }
    } catch (error) {
        showToast("error", `Error in Delete Dashboard:, ${error}`);
    } finally {
        setIsLoading(false);
        setIsDeleteModal(false);
        setRevokeTarget(null);
    }
};

const saveClientUserDetails = async ({
    emailAddress,
    basecountryID,
    accessCountries,
    editClientID,
    originalcountryIds,
    setValidate,
    setIsLoading,
    showToast,
    navigate
}) => {
    setValidate(true);

    const countryAccessTemp = accessCountries?.filter(x => x.defaultChecked)
        .map(x => x.code)
        .join(",") || "";

    if (!isClientFormValid(emailAddress, basecountryID, countryAccessTemp)) return;

    setIsLoading(true);
    const isUpdating = editClientID > 0;
    const userPayload = createUserPayload(emailAddress, basecountryID, countryAccessTemp, editClientID, originalcountryIds);

    try {
        const response = await axiosPost(isUpdating ? UpdateUserDetailsUrl : InsertUserDetailsUrl, userPayload);
        if (response?.code !== 200) {
            showToast("error", response?.responseMsg);
            return;
        }
        setValidate(false);
        const actionLabel = isUpdating ? "Updated" : "Saved";
        showToast("success", `Successfully ${actionLabel}.`);
        setTimeout(() => navigate(CLIENT_LIST_PATH), 1000);
    } catch (error) {
        const verb = isUpdating ? "updating" : "saving";
        console.error(`Error ${verb} user:`, error);
    } finally {
        setIsLoading(false);
    }
};

const applyClientFieldChange = ({
    value,
    field,
    accessCountries,
    setEmailAddress,
    setBasecountryID,
    setAccessCountries
}) => {
    if (field === "email") {
        setEmailAddress(value);
        return;
    }
    if (field === "country") {
        setBasecountryID(value);
        if (value > 0) {
            setAccessCountries(updateCountryCheckedState(accessCountries, value, true));
        }
    }
};

const applyAccessCountrySelection = ({
    code,
    checked,
    basecountryID,
    accessCountries,
    setBasecountryID,
    setAccessCountries
}) => {
    if (Number(code) === Number(basecountryID) && !checked) {
        setBasecountryID("0");
    }
    setAccessCountries(updateCountryCheckedState(accessCountries, code, checked));
};

const renderAccessibleDashboardsRows = ({ list, invalidDateText, noRecordFoundText, revokeAccessText, onDelete }) => {
    if (!list?.length) {
        return (
            <TableRow>
                <TableCell colSpan={5}>{noRecordFoundText}</TableCell>
            </TableRow>
        );
    }
    return list.map((item) => (
        <TableRow key={`${item.dashboardGroupID}-${item.dashboardID}`}>
            <TableCell className="padding-left-none">{item.dashboardName}</TableCell>
            <TableCell>{item.dashboardGroup}</TableCell>
            <TableCell>{item.accessRight}</TableCell>
            <TableCell>{formatValidUntilDate(item.validUntil, invalidDateText)}</TableCell>
            <TableCell align="center">
                <Button className="clientActionBtn" onClick={() => onDelete(item.dashboardID)}>
                    <Tooltip title={revokeAccessText}>
                        <Icon
                            className="fa-light fa-trash-can font-size-base text-primary btn-list"
                        />
                    </Tooltip>
                </Button>
            </TableCell>
        </TableRow>
    ));
};

const renderAccessCountryCheckboxes = ({
    accessCountries,
    showAccessCountryError,
    pleaseSelectOneCountryText,
    onAccessCountryChange,
    disabled = false
}) =>
    accessCountries.map((country, index) => (
        <FormControl className="margin-top-s clientMarginLeft" key={country.code} error={showAccessCountryError}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={country.defaultChecked}
                        disabled={disabled}
                        onChange={(e) => onAccessCountryChange(country.code, e.target.checked)}
                    />
                }
                label={country.description}
            />
            {index === accessCountries.length - 1 && showAccessCountryError && (
                <FormHelperText>
                    {pleaseSelectOneCountryText}
                </FormHelperText>
            )}
        </FormControl>
    ));

const ClientUserManage = () => {
    const { t } = useTranslation();
    const { ToastComponent, showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const editClientID = getEditClientID(location);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [isDeleteAccountModal, setIsDeleteAccountModal] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [dashboardGrpList, setDashboardGrpList] = useState([]);
    const [emailAddress, setEmailAddress] = useState("");
    const [basecountryID, setBasecountryID] = useState("");
    const [activeDashboard, setActiveDashboard] = useState("");
    const [viewedDashboard, setViewedDashboard] = useState("");
    const [lastLoginDate, setLastLoginDate] = useState("");
    const [status, setStatus] = useState("");

    const [clientCountryFillList, setClientCountryFillList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const listItemsPerPage = 10;
    const totalItems = dashboardGrpList?.length;
    const startIndex = (currentPage - 1) * listItemsPerPage;
    const paginatedDashboardGrpList = dashboardGrpList?.slice(startIndex, startIndex + listItemsPerPage);

    const TAction = t("common.Action");
    const TnoRecordFound = t("common.noRecordFound");
    const Tdelete = t("common.Delete");
    const Tdeactivate = t("common.deactivate");
    const Tcancel = t("common.Cancel");
    const Tsave = t("common.Save");
    const Tdashboard = t("common.Dashboard");
    const TinvalidDate = t("common.invalidDate");
    const Tstatus = t("common.status");
    const Tyes = t("common.yes");
    const Tno = t("common.no");
    const TpageTitle = t("clientPage.pageTitle");
    const Temail = t("clientPage.Email");
    const TenterEmail = t("clientPage.addEmail");
    const TclientDetails = t("clientPage.clientDetails");
    const TActiveDashboards = t("clientPage.ActiveDashboards");
    const TViewedDashboards = t("clientPage.ViewedDashboards");
    const TListofAccessibleDashboards = t("clientPage.ListofAccessibleDashboards");
    const TGroup = t("clientPage.Group");
    const TAccessRight = t("clientPage.AccessRight");
    const TvalidTill = t("clientPage.validTill");
    const TdeactivateAccount = t("clientPage.deactivateAccount");
    const TdeactivateAccountConfirm = t("clientPage.deactivateAccountConfirmation");
    const TdeactivateAccountSuccess = t("clientPage.deactivateAccountSuccess");
    const TdeleteAccount = t("clientPage.deleteAccount");
    const TdeleteAccountConfirm = t("clientPage.deleteAccountConfirmation");
    const TdeleteAccountSuccess = t("clientPage.deleteAccountSuccess");
    const TlastLoginDate = t("clientPage.lastLoginDate");
    const TneverLoggedIn = t("clientPage.neverLoggedIn");
    const TrevokeAccess = t("clientPage.revokeAccess");
    const TrevokeAccessConfirm = t("clientPage.revokeAccessConfirmation");
    const TaccessRevokedSuccess = t("clientPage.accessRevokedSuccess");

    const isClientInactive = Boolean(status?.trim()) && status.trim().toLowerCase() !== "active";
    const isAdminUser = isAdminRole(localStorage.getItem("userInfo"));

    const [validate, setValidate] = useState(false);
    const [originalcountryIds, setOriginalcountryIds] = useState("");
    const [accessCountries, setAccessCountries] = useState([]);

    const resetClientState = useCallback(() => {
        setEmailAddress("");
        setBasecountryID("");
        setActiveDashboard("");
        setViewedDashboard("");
        setLastLoginDate("");
        setStatus("");
        setDashboardGrpList([]);
    }, []);

    const formatLastLogin = useCallback(
        (rawDate) => formatLastLoginDate(rawDate, TinvalidDate, TneverLoggedIn),
        [TinvalidDate, TneverLoggedIn]
    );

    const getClientDetails = useCallback(
        (clientID, updatedAccessCountries = []) =>
            loadClientDetailsData({
                clientID,
                updatedAccessCountries,
                setIsLoading,
                setLastLoginDate,
                setStatus,
                resetClientState,
                setEmailAddress,
                setBasecountryID,
                setActiveDashboard,
                setViewedDashboard,
                formatLastLogin,
                setDashboardGrpList,
                setAccessCountries,
                setOriginalcountryIds
            }),
        [resetClientState, formatLastLogin]
    );

    const fetchData = useCallback(
        () =>
            loadInitialClientData({
                userID: editClientID,
                setIsLoading,
                setClientCountryFillList,
                setAccessCountries,
                getClientDetails
            }),
        [getClientDetails, editClientID]
    );

    useEffect(() => {
        if (parseAndValidateUserInfo(navigate)) {
            fetchData();
        }
    }, [fetchData, navigate]);

    const toggleDeleteModal = () => {
        setIsDeleteModal((prev) => !prev);
    };

    const toggleDeleteAccountModal = () => {
        setIsDeleteAccountModal((prev) => !prev);
    };

    const handleDisable = (userID) =>
        deactivateClientUser({
            userID,
            deactivateAccountSuccessText: TdeactivateAccountSuccess,
            accessCountries,
            setIsLoading,
            setIsDeleteModal,
            setStatus,
            setActiveDashboard,
            setDashboardGrpList,
            showToast,
            getClientDetails
        });

    const handleDeleteAccount = (userID) =>
        deleteClientAccountPermanently({
            userID,
            deleteAccountSuccessText: TdeleteAccountSuccess,
            setIsLoading,
            setIsDeleteAccountModal,
            showToast,
            navigate
        });

    const handleFieldChange = (e, field) => {
        if (isClientInactive) return;
        return applyClientFieldChange({
            value: e.target.value,
            field,
            accessCountries,
            setEmailAddress,
            setBasecountryID,
            setAccessCountries
        });
    };

    const openRevokeModal = (dashboardID) => {
        setRevokeTarget({ dashboardID });
    };

    const closeRevokeModal = () => {
        setRevokeTarget(null);
    };

    const handleDelete = (dashboardID) =>
        revokeClientDashboardAccess({
            dashboardID,
            editClientID,
            accessRevokedSuccessText: TaccessRevokedSuccess,
            formatLastLogin,
            setIsLoading,
            setIsDeleteModal,
            setRevokeTarget,
            setLastLoginDate,
            setStatus,
            setDashboardGrpList,
            setActiveDashboard,
            setViewedDashboard,
            showToast
        });

    const navigateCancel = () => {
        navigate(CLIENT_LIST_PATH);
    };

    const {
        showEmailError,
        showCountryError,
        showAccessCountryError,
        errorMessageEmail,
        errorMessageCountry
    } = computeValidationState({
        validate,
        emailAddress,
        basecountryID,
        accessCountries,
        pleaseSelectCountryText: t("user_mgmt.pleaseSelectCountry"),
        pleaseEnterEmailText: t("user_mgmt.pleaseEnterEmail")
    });

    const handleSave = () => {
        if (isClientInactive) return;
        return saveClientUserDetails({
            emailAddress,
            basecountryID,
            accessCountries,
            editClientID,
            originalcountryIds,
            setValidate,
            setIsLoading,
            showToast,
            navigate
        });
    };

    const handleAccessCountryChange = (code, checked) => {
        if (isClientInactive) return;
        return applyAccessCountrySelection({
            code,
            checked,
            basecountryID,
            accessCountries,
            setBasecountryID,
            setAccessCountries
        });
    };

    return (
        <>
            {isLoading && <Loader />}
            <HeaderBlock left="" title={TpageTitle} leftCenterAlign="true" actionsCenterAlign="true" />

            <main
                id="main"
                className="clientMain padding-bottom-base"
            >
                <Box>
                    <Box className="clientNoBorder margin-bottom-m">
                        <Card className="padding-top-s padding-left-base shadow-0 border-light border-radius-soft border-radius padding-bottom-none">
                            <CardContent>
                                <Typography className={SECTION_HEADER_CLASS} variant="h6" gutterBottom>
                                    {TclientDetails}
                                </Typography>

                                <Box className="clientHeight" />
                                <Grid2 container spacing={2} className="margin-bottom-l">
                                    <Grid2 size={{ xs: 6, sm: 3 }}>
                                        <Box className={STAT_CARD_CLASS}>
                                            <Typography variant="h5" className="text-primary font-bold">
                                                {activeDashboard ?? 0}
                                            </Typography>
                                            <Typography className="text-primary-dark">{TActiveDashboards}</Typography>
                                        </Box>
                                    </Grid2>
                                    <Grid2 size={{ xs: 6, sm: 3 }}>
                                        <Box className={STAT_CARD_CLASS}>
                                            <Typography variant="h5" className="text-primary font-bold">
                                                {viewedDashboard ?? 0}
                                            </Typography>
                                            <Typography className="text-primary-dark">{TViewedDashboards}</Typography>
                                        </Box>
                                    </Grid2>
                                </Grid2>
                                {/* Filters */}
                                <Grid2 container spacing={2} className="margin-bottom-m">
                                    <Grid2 size={{ xs: 12, sm: 3 }}>
                                        <TextField
                                            label={Temail}
                                            variant="outlined"
                                            className="clientFormField"
                                            placeholder={TenterEmail}
                                            value={emailAddress}
                                            onChange={(e) => handleFieldChange(e, "email")}
                                            disabled={isClientInactive}
                                            required
                                            slotProps={{
                                                inputLabel: {
                                                    shrink: true,
                                                },
                                            }}
                                            error={showEmailError}
                                            helperText={errorMessageEmail}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 3 }}>
                                        <TextField
                                            label="Country"
                                            variant="outlined"
                                            select
                                            fullWidth
                                            className="clientFormField"
                                            placeholder="Select Country"
                                            value={basecountryID}
                                            onChange={(e) => handleFieldChange(e, "country")}
                                            disabled={isClientInactive}
                                            required
                                            slotProps={{
                                                inputLabel: {
                                                    shrink: true,
                                                },
                                            }}
                                            error={showCountryError}
                                            helperText={errorMessageCountry}
                                        >
                                            {clientCountryFillList.map((role) => (
                                                <MenuItem key={role.code} value={role.code}>
                                                    {role.description}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 3 }}>
                                        <TextField
                                            label={TlastLoginDate}
                                            variant="outlined"
                                            fullWidth
                                            className={READONLY_FIELD_CLASS}
                                            value={lastLoginDate}
                                            slotProps={READONLY_FIELD_SLOT_PROPS}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 3 }}>
                                        <TextField
                                            label={Tstatus}
                                            variant="outlined"
                                            fullWidth
                                            className={READONLY_FIELD_CLASS}
                                            value={status}
                                            slotProps={READONLY_FIELD_SLOT_PROPS}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 12 }} className="padding-top-none">
                                        <Typography variant="h6" className={`padding-bottom-none ${SECTION_HEADER_CLASS}`}>
                                            {t("user_mgmt.accessibleCountry")} <span className="clientColorRed">&nbsp;*</span>
                                        </Typography>
                                        {renderAccessCountryCheckboxes({
                                            accessCountries,
                                            showAccessCountryError,
                                            pleaseSelectOneCountryText: t("user_mgmt.pleaseSelectOneCountry"),
                                            onAccessCountryChange: handleAccessCountryChange,
                                            disabled: isClientInactive
                                        })}
                                    </Grid2>
                                </Grid2>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box className="site-border border-light padding-y-base padding-x-base border-radius" >
                        <Typography className="font-size-h5 text-primary margin-bottom-xs text-uppercase " variant="h6" gutterBottom>
                            {TListofAccessibleDashboards}
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="padding-left-none">{Tdashboard}</TableCell>
                                        <TableCell>{TGroup}</TableCell>
                                        <TableCell>{TAccessRight}</TableCell>
                                        <TableCell>{TvalidTill}</TableCell>
                                        <TableCell align="center">{TAction}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {renderAccessibleDashboardsRows({
                                        list: paginatedDashboardGrpList,
                                        invalidDateText: TinvalidDate,
                                        noRecordFoundText: TnoRecordFound,
                                        revokeAccessText: TrevokeAccess,
                                        onDelete: openRevokeModal
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <CommonPagination
                            totalItems={totalItems}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    </Box>
                </Box>
                {/* Buttons */}
                <Box className="display-flex justify-content-center margin-top-base clientManageActions">
                    <Button variant="contained" color="error" onClick={toggleDeleteModal} disabled={!editClientID || isClientInactive}>
                        {TdeactivateAccount}
                    </Button>
                    {isAdminUser && (
                        <Button variant="contained" color="error" onClick={toggleDeleteAccountModal} disabled={!editClientID}>
                            {TdeleteAccount}
                        </Button>
                    )}
                    <Button variant="outlined" color="primary" onClick={() => navigateCancel()}>
                        {Tcancel}
                    </Button>
                    <Button variant="contained" color="primary" onClick={() => handleSave()} disabled={isClientInactive}>
                        {Tsave}
                    </Button>
                </Box>

            </main>

            <Dialog
                open={isDeleteModal}
                onClose={toggleDeleteModal}
                className="clientDialog"
            >
                <DialogTitle>
                    <Typography
                        variant="h6"
                        component="span"
                        className="clientDialogTitle font-bold"
                    >
                        {TdeactivateAccount}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography className="clientDialogContent">
                        {TdeactivateAccountConfirm}
                    </Typography>
                </DialogContent>

                <DialogActions className="dialogAction">
                    <Button className={DIALOG_BTN_CLASS}
                        variant="outlined"
                        onClick={toggleDeleteModal}
                    >
                        {Tcancel}
                    </Button>
                    <Button className={DIALOG_BTN_CLASS}
                        variant="contained"
                        onClick={() => handleDisable(editClientID)}
                    >
                        {Tdeactivate}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isDeleteAccountModal}
                onClose={toggleDeleteAccountModal}
                className="clientDialog"
            >
                <DialogTitle>
                    <Typography
                        variant="h6"
                        component="span"
                        className="clientDialogTitle font-bold"
                    >
                        {TdeleteAccount}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography className="clientDialogContent">
                        {TdeleteAccountConfirm}
                    </Typography>
                </DialogContent>

                <DialogActions className="dialogAction">
                    <Button className={DIALOG_BTN_CLASS}
                        variant="outlined"
                        onClick={toggleDeleteAccountModal}
                    >
                        {Tcancel}
                    </Button>
                    <Button className={DIALOG_BTN_CLASS}
                        variant="contained"
                        onClick={() => handleDeleteAccount(editClientID)}
                    >
                        {Tdelete}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(revokeTarget)}
                onClose={closeRevokeModal}
                className="clientDialog"
            >
                <DialogTitle>
                    <Typography
                        variant="h6"
                        component="span"
                        className="clientDialogTitle font-bold"
                    >
                        {TrevokeAccess}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography className="clientDialogContent">
                        {TrevokeAccessConfirm}
                    </Typography>
                </DialogContent>

                <DialogActions className="dialogAction">
                    <Button
                        className={DIALOG_BTN_CLASS}
                        variant="outlined"
                        onClick={closeRevokeModal}
                    >
                        {Tno}
                    </Button>
                    <Button
                        className={DIALOG_BTN_CLASS}
                        variant="contained"
                        onClick={() => handleDelete(revokeTarget.dashboardID)}
                    >
                        {Tyes}
                    </Button>
                </DialogActions>
            </Dialog>
            {ToastComponent}
        </>
    );
};

export default ClientUserManage;
